import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { storesService } from "@/services/storesService";
import { productsService } from "@/services/productsService";
import type { AdminStoreCreateDto, AdminStoreUpdateDto } from "@/types/store.dto";
import type { AdminProductCreateDto, ProductApprovalPayload, ProductUpdateDto } from "@/types/product.dto";
import { deliveryPartnersService } from "@/services/deliveryPartnersService";
import { walletsService } from "@/services/walletsService";
import { referralsService } from "@/services/referralsService";
import {
  mapDeliveryPartnerListDtosToAdmin,
  mapProductListDtosToAdminProducts,
  mapStoreListDtosToAdminStores,
} from "@/lib/mappers/admin-mappers";
import { mapSettlementsToAdminRows } from "@/lib/mappers/account-mappers";
import type { SettlementFilterParams } from "@/types/wallet.dto";
import type { ProcessSettlementDto } from "@/types/wallet.dto";
import { ReferralType } from "@/types/enums";
import { queryKeys } from "./keys";

export function useAdminStores(page = 1, pageSize = 100) {
  return useQuery({
    queryKey: queryKeys.admin.stores(page, pageSize),
    queryFn: async () => {
      const result = await storesService.getAll(page, pageSize);
      return {
        ...result,
        items: mapStoreListDtosToAdminStores(result.items),
      };
    },
  });
}

export function usePendingStores(page = 1, pageSize = 50) {
  return useQuery({
    queryKey: queryKeys.admin.pendingStores,
    queryFn: async () => {
      const result = await storesService.getPending(page, pageSize);
      return mapStoreListDtosToAdminStores(result.items);
    },
  });
}

export function useApproveStoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approve, rejectionReason }: { id: number; approve: boolean; rejectionReason?: string }) =>
      storesService.approve(id, approve, rejectionReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stores"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "store"] });
    },
  });
}

export function useSuspendStoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => storesService.suspend(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stores"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "store"] });
    },
  });
}

export function useAdminProducts() {
  return useQuery({
    queryKey: queryKeys.admin.products,
    queryFn: async () => {
      const result = await productsService.getAdminAll(1, 500);
      return mapProductListDtosToAdminProducts(result.items ?? []);
    },
    retry: 1,
  });
}

export function useApproveProductMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: { id: number } & ProductApprovalPayload) =>
      productsService.approve(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "product"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useSetProductVisibilityMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      productsService.setVisibility(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "product"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useDeleteAdminProductMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => productsService.deleteByAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "product"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useCreateAdminProductMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AdminProductCreateDto) => productsService.createByAdmin(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stores"] });
    },
  });
}

export function useUpdateAdminProductMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: ProductUpdateDto }) =>
      productsService.updateByAdmin(id, dto),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "product", vars.id] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useCreateAdminStoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AdminStoreCreateDto) => storesService.createByAdmin(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stores"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "store"] });
    },
  });
}

export function useUpdateAdminStoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: AdminStoreUpdateDto }) =>
      storesService.updateByAdmin(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stores"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "store"] });
    },
  });
}

export function useDeleteAdminStoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => storesService.deleteByAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "stores"] });
    },
  });
}

export function useAdminStoreDetail(storeId: number) {
  return useQuery({
    queryKey: ["admin", "store", storeId],
    queryFn: async () => {
      const [store, dashboard, products] = await Promise.all([
        storesService.getById(storeId),
        storesService.getDashboard(storeId),
        productsService.getByStore(storeId, 1, 10),
      ]);
      return { store, dashboard, products };
    },
    enabled: storeId > 0,
  });
}

export function useAdminDeliveryPartners(page = 1, pageSize = 100) {
  return useQuery({
    queryKey: queryKeys.admin.deliveryPartners(page, pageSize),
    queryFn: async () => {
      const result = await deliveryPartnersService.getAll(page, pageSize);
      return {
        ...result,
        items: mapDeliveryPartnerListDtosToAdmin(result.items),
      };
    },
  });
}

export function useAdminDeliveryPartnerDetail(partnerId: number) {
  return useQuery({
    queryKey: ["admin", "delivery", partnerId],
    queryFn: () => deliveryPartnersService.getById(partnerId),
    enabled: partnerId > 0,
  });
}

export function useApproveDeliveryPartnerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      approve,
      rejectionReason,
      commissionPerDelivery,
    }: {
      id: number;
      approve: boolean;
      rejectionReason?: string;
      commissionPerDelivery?: number;
    }) => deliveryPartnersService.approve(id, { approve, rejectionReason, commissionPerDelivery }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "delivery"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "delivery", vars.id] });
    },
  });
}

export function useSuspendDeliveryPartnerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      deliveryPartnersService.suspend(id, reason),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "delivery"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "delivery", vars.id] });
    },
  });
}

export function useAdminSettlements(filter: SettlementFilterParams = { pageSize: 100 }) {
  return useQuery({
    queryKey: queryKeys.admin.settlements(filter),
    queryFn: async () => {
      const result = await walletsService.getSettlements(filter);
      return {
        ...result,
        items: mapSettlementsToAdminRows(result.items),
      };
    },
  });
}

export function useProcessSettlementMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: ProcessSettlementDto }) =>
      walletsService.processSettlement(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "settlements"] });
    },
  });
}

export function useFailSettlementMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      walletsService.failSettlement(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "settlements"] });
    },
  });
}

export function useReferralLevelConfigs(type: ReferralType = ReferralType.User) {
  return useQuery({
    queryKey: queryKeys.admin.referralLevels(type),
    queryFn: () => referralsService.getLevelConfigs(type),
  });
}

export function useCreateReferralLevelConfigMutation(type: ReferralType = ReferralType.User) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: ReferralLevelConfigCreateDto) => referralsService.createLevelConfig(dto),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: queryKeys.admin.referralLevels(type) });
    },
  });
}

export function useUpdateReferralLevelConfigMutation(type: ReferralType = ReferralType.User) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: ReferralLevelConfigCreateDto }) =>
      referralsService.updateLevelConfig(id, dto),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: queryKeys.admin.referralLevels(type) });
    },
  });
}

export function useDeleteReferralLevelConfigMutation(type: ReferralType = ReferralType.User) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => referralsService.deleteLevelConfig(id),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: queryKeys.admin.referralLevels(type) });
    },
  });
}

export function useToggleReferralLevelConfigMutation(type: ReferralType = ReferralType.User) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      referralsService.toggleLevelConfig(id, isActive),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: queryKeys.admin.referralLevels(type) });
    },
  });
}

export function useAdminNavBadges() {
  const { data: pendingStores = [] } = usePendingStores();
  const { data: products = [] } = useAdminProducts();

  return {
    pendingStoreApprovals: pendingStores.length,
    pendingProductApprovals: products.filter((p) => p.status === "pending").length,
  };
}
